import { firebase } from "../firebase";

export interface SubscriptionPeriodRow {
  id: number;
  account_id: string | null;
  platform: string;
  original_transaction_id: string;
  transaction_id: string;
  product_id: string | null;
  plan: string | null;
  start_time: Date | string;
  end_time: Date | string;
  effective_end_time: Date | string | null;
  is_trial: boolean | null;
  is_trial_conversion: boolean | null;
  renewal_number: number | null;
  refunded_at: Date | string | null;
  unsubscribe_detected_at: Date | string | null;
  billing_issues_detected_at: Date | string | null;
  amount_cents: number | null;
  amount_usd_cents: number | null;
  effective_cents: number | null;
  effective_usd_cents: number | null;
  currency: string | null;
  commission_pct: number | null;
  tax_pct: number | null;
  country: string | null;
  storefront: string | null;
  source: string | null;
  raw: any;
  transferred_from_account_id: string | null;
  transferred_at: Date | string | null;
}

export interface SubscriptionPeriodsResponse {
  periods: SubscriptionPeriodRow[];
}

// `effective_*` is the refund-adjusted amount collected (a refunded period has
// effective 0, a partial refund is prorated). It's also stored pre-tax on every
// platform (Square strips tax into `tax_pct`; Stripe/RevenueCat amounts are
// tax-exclusive), so tax never has to be subtracted again here.

// Refund-adjusted revenue collected, before platform commission.
export const periodGrossUsdCents = (p: SubscriptionPeriodRow): number =>
  p.effective_usd_cents ?? p.amount_usd_cents ?? 0;

// Gross minus platform commission.
export const periodNetUsdCents = (p: SubscriptionPeriodRow): number => {
  const commission = p.commission_pct ?? 0;
  return Math.round(periodGrossUsdCents(p) * (1 - commission));
};

export const fetchAccountSubscriptionHistory = async (
  accountId: string,
): Promise<SubscriptionPeriodsResponse> => {
  const currentUser = firebase.auth().currentUser;
  const token = await currentUser?.getIdToken(true);

  const response = await fetch(`cs/subscriptionPeriods?accountId=${encodeURIComponent(accountId)}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accepts: "application/json",
    },
  });

  const dt = await response.json();
  if (!response.ok || (response.status > 200 && response.status < 600)) {
    throw new Error(dt.error || response.statusText);
  }
  return dt;
};
